import type { useNavigate } from '@tanstack/react-router';

import { useUiStore } from '@/features/settings/ui-store.js';
import {
  createConversation,
  deleteConversation,
  resetChatStreamGuard,
} from '@/integration/services/integration-api.js';
import { useChatSessionStore } from '@/integration/state/chat-session-store.js';
import type { Skill } from '@/lib/api/skills.js';

import { SKILL_CREATOR_ID } from './skill-creator.js';

type NavigateFn = ReturnType<typeof useNavigate>;

/**
 * Opens a new chat in `workspaceId` with the skill creator attached and the composer prefilled with
 * a request to improve `skill`. The text is prefilled, not sent, so the user adds what is wrong.
 * Clears every pending composer state first, the same way `openInAgent` does, so nothing from an
 * earlier chat rides along. The prefill is addressed to the new conversation: over the Home page the
 * Home composer stays mounted even after navigation resolves, and it would otherwise take the prefill
 * and unmount with it, so the chat composer would open empty. If navigation fails, the new
 * conversation is deleted so no empty one is left.
 */
export async function openSkillCreatorChat({
  skill,
  workspaceId,
  navigate,
}: {
  skill: Skill;
  workspaceId: string | null;
  navigate: NavigateFn;
}): Promise<void> {
  const conversation = await createConversation({
    title: `Improve ${skill.title}`,
    workspaceId,
  });
  resetChatStreamGuard();
  const ui = useUiStore.getState();
  const session = useChatSessionStore.getState();
  session.setActiveConversationId(null);
  session.setStreamingConvId(null);
  ui.setPendingDraft(null);
  ui.setPendingAttachments([]);
  ui.setPendingConversationId(null);
  ui.setPendingFiles([]);
  ui.setPendingMic(false);
  ui.setPendingSkillIds([]);
  ui.setPendingSkill(null);
  ui.setPendingSkillsDirectoryId(null);
  ui.setPendingArtifactAttachments([]);
  ui.setPendingSharePointAttachments([]);
  ui.setPendingOneDriveAttachments([]);
  ui.setComposerPrefill(null);
  ui.setComposerPrefillAttachments([]);
  ui.setComposerPrefillSkillIds([]);
  ui.setComposerPrefillRestoresDraft(false);
  if (workspaceId) ui.setSelectedWorkspaceId(workspaceId);
  try {
    await navigate({
      to: '/chats',
      search: { conversationId: conversation.id },
    });
  } catch (error) {
    await deleteConversation(conversation.id).catch(() => undefined);
    throw error;
  }
  ui.setComposerPrefillSkillIds([SKILL_CREATOR_ID]);
  ui.setComposerPrefillRestoresDraft(true);
  ui.setComposerPrefill(
    `Improve my skill /${skill.trigger}: `,
    conversation.id,
  );
}
